import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LogOut, GraduationCap, Megaphone, UserCircle, School, Calendar, CreditCard, BookOpen } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('parent_token');

  useEffect(() => {
    if (!token) { navigate('/parent/login'); return; }
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/parent/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('parent_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('parent_user');
    navigate('/parent/login');
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="h-8 w-8 border-4 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
    </div>
  );

  const parent = data?.parent;
  const students = data?.students || [];
  const school = data?.school;
  const announcements = data?.announcements || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="parent-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4" data-testid="parent-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-violet-500 flex items-center justify-center">
              <UserCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-900">{parent?.full_name}</p>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <School className="h-3 w-3" />
                <span>{school?.name}</span>
                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{school?.school_code}</span>
              </div>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-slate-500 hover:text-red-500" data-testid="parent-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        {/* Children Cards */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-violet-500" /> My Children
          </h2>
          {students.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <p className="text-slate-400">No children linked to your account</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {students.map(s => (
                <div key={s.student_id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`child-card-${s.student_id}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`h-14 w-14 rounded-xl flex items-center justify-center text-xl font-bold text-white ${s.gender === 'male' ? 'bg-blue-400' : s.gender === 'female' ? 'bg-pink-400' : 'bg-slate-400'}`}>
                      {s.first_name?.[0]}{s.last_name?.[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-lg">{s.full_name}</h3>
                      <p className="text-sm text-slate-400">Adm: {s.admission_number}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <BookOpen className="h-4 w-4 text-teal-500 mx-auto mb-1" />
                      <p className="text-xs text-slate-400">Class</p>
                      <p className="text-sm font-semibold text-slate-700">{s.class_name} {s.section}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <Calendar className="h-4 w-4 text-blue-500 mx-auto mb-1" />
                      <p className="text-xs text-slate-400">Roll No</p>
                      <p className="text-sm font-semibold text-slate-700">{s.roll_number || '-'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <CreditCard className="h-4 w-4 text-amber-500 mx-auto mb-1" />
                      <p className="text-xs text-slate-400">Status</p>
                      <p className={`text-sm font-semibold capitalize ${s.status === 'active' ? 'text-emerald-600' : 'text-red-500'}`}>{s.status}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Quick Links */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Quick Access</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Attendance', icon: Calendar, color: 'bg-blue-50 text-blue-600', to: '/parent/attendance' },
              { label: 'Report Card', icon: BookOpen, color: 'bg-emerald-50 text-emerald-600', to: '/parent/report-card' },
              { label: 'Fee Payment', icon: CreditCard, color: 'bg-amber-50 text-amber-600', to: '/parent/fees' },
              { label: 'Timetable', icon: GraduationCap, color: 'bg-violet-50 text-violet-600', to: null },
            ].map(item => item.to ? (
              <Link key={item.label} to={item.to} className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:shadow-md transition-shadow block" data-testid={`quick-link-${item.label.toLowerCase().replace(' ', '-')}`}>
                <item.icon className={`h-6 w-6 mx-auto mb-2 ${item.color.split(' ')[1]}`} />
                <p className="text-sm font-medium text-slate-700">{item.label}</p>
              </Link>
            ) : (
              <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-4 text-center opacity-60 cursor-not-allowed relative" data-testid={`quick-link-${item.label.toLowerCase().replace(' ', '-')}`}>
                <item.icon className={`h-6 w-6 mx-auto mb-2 ${item.color.split(' ')[1]}`} />
                <p className="text-sm font-medium text-slate-700">{item.label}</p>
                <span className="absolute top-2 right-2 text-[10px] bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full">Coming Soon</span>
              </div>
            ))}
          </div>
        </section>

        {/* Announcements */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-amber-500" /> School Announcements
          </h2>
          {announcements.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <p className="text-slate-400 text-sm">No announcements at this time</p>
            </div>
          ) : (
            <div className="space-y-3">
              {announcements.map((a, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`announcement-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`h-2 w-2 rounded-full ${a.priority === 'urgent' ? 'bg-red-400' : a.priority === 'important' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                    <h4 className="font-semibold text-sm text-slate-900">{a.title}</h4>
                  </div>
                  <p className="text-sm text-slate-500 ml-4">{a.message}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
